import { createElement, Fragment } from 'react'
import type { ReactNode } from 'react'

/**
 * HTML del widget de Civitatis con lista blanca (D55; mismo enfoque que `richText`, D49). La app actual lo sanea con
 * DOMPurify y lo inyecta con `innerHTML`, dejando pasar `<link>` (la hoja de estilos de Civitatis, que afecta a toda la
 * página). v2 recorre el HTML con DOMParser y crea elementos de React; nunca inyecta HTML.
 *
 * Como la app actual:
 * - Los enlaces relativos del widget apuntan a `https://civitatis.com` y se abren aparte.
 * - `srcset` relativos, a `https://civitatis.com`.
 * - Las tarjetas "free" van primero.
 *
 * Lista blanca:
 * - Etiquetas: estructura (div, span, p, section, article, ul, ol, li), texto (strong, b, em, i, small, br) y medios
 *   (a, img, picture, source). Los títulos pasan a `strong` para no romper el orden de títulos de la pantalla.
 * - Atributos: `class` (el widget no trae su hoja de estilos, así que no pinta nada, pero no estorba), `href` https,
 *   `src` e `srcset` https, `alt`, y `type`/`media` en `source`. Nada más: ni `style`, ni eventos, ni `data-*`.
 * - Fuera con su contenido: script, style, link, iframe, object, embed, form y similares.
 */
const BASE = 'https://civitatis.com'
const KEEP = new Set(['div', 'span', 'p', 'section', 'article', 'ul', 'ol', 'li', 'strong', 'em', 'small', 'br', 'a', 'img', 'picture', 'source'])
const ALIAS: Record<string, string> = { b: 'strong', i: 'em' }
const HEADINGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6'])
const DROP = new Set(['script', 'style', 'link', 'meta', 'iframe', 'object', 'embed', 'template', 'noscript', 'svg', 'math', 'form', 'input', 'button', 'select', 'textarea', 'video', 'audio', 'head', 'title'])
const HTTPS = /^https:\/\//i

const absolute = (url: string) => (url.startsWith('/') && !url.startsWith('//') ? `${BASE}${url}` : url)
const safeSrcset = (value: string) => {
  const parts = value
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      const [url = '', ...rest] = p.split(/\s+/)
      return [absolute(url), ...rest].join(' ')
    })
  return parts.every((p) => HTTPS.test(p)) ? parts.join(', ') : null
}

function attributes(el: Element, tag: string): Record<string, string> | null {
  const props: Record<string, string> = {}
  const cls = el.getAttribute('class')
  if (cls) props.className = cls
  if (tag === 'a') {
    const href = absolute(el.getAttribute('href')?.trim() ?? '')
    if (!HTTPS.test(href)) return props // sin enlace seguro: se desenvuelve
    return { ...props, href, target: '_blank', rel: 'noopener noreferrer' }
  }
  if (tag === 'img') {
    const src = absolute(el.getAttribute('src')?.trim() ?? '')
    if (!HTTPS.test(src)) return null
    return { ...props, src, alt: el.getAttribute('alt') ?? '' }
  }
  if (tag === 'source') {
    const srcset = safeSrcset(el.getAttribute('srcset') ?? '')
    if (!srcset) return null
    const type = el.getAttribute('type')
    const media = el.getAttribute('media')
    return { ...props, srcSet: srcset, ...(type ? { type } : {}), ...(media ? { media } : {}) }
  }
  return props
}

function convert(node: Node, key: string): ReactNode {
  if (node.nodeType === 3) return node.textContent
  if (node.nodeType !== 1) return null
  const el = node as Element
  const raw = el.tagName.toLowerCase()
  if (DROP.has(raw)) return null
  const children = Array.from(el.childNodes).map((child, i) => convert(child, `${key}.${i}`))
  if (HEADINGS.has(raw)) return createElement('strong', { key, ...(el.getAttribute('class') ? { className: el.getAttribute('class')! } : {}) }, ...children)
  const tag = ALIAS[raw] ?? raw
  if (!KEEP.has(tag)) return createElement(Fragment, { key }, ...children)
  const props = attributes(el, tag)
  if (props === null) return null
  if (tag === 'a' && !props.href) return createElement(Fragment, { key }, ...children)
  if (tag === 'img' || tag === 'source' || tag === 'br') return createElement(tag, { key, ...props })
  return createElement(tag, { key, ...props }, ...children)
}

/** Nodos de React del widget y número de actividades; sin actividades, `count` es 0 y la pantalla no pinta el bloque. */
export function partnerWidget(html: string): { nodes: ReactNode; count: number } {
  if (typeof DOMParser === 'undefined') return { nodes: null, count: 0 }
  const doc = new DOMParser().parseFromString(html, 'text/html')
  // Como la app actual: las actividades gratuitas primero.
  for (const card of Array.from(doc.querySelectorAll('.cosy-card'))) {
    const title = card.querySelector('.cosy-card__title')?.textContent ?? ''
    const item = card.parentElement
    if (/free/i.test(title) && item?.parentElement) item.parentElement.prepend(item)
  }
  const count = doc.querySelectorAll('.cosy-card').length
  return { nodes: Array.from(doc.body.childNodes).map((child, i) => convert(child, String(i))), count }
}
